import { useSelector } from 'react-redux';
import { colors, Icon, Tooltip, Txt } from '@vst/beam';
import { AddOutlined, DownloadOutlined, HelpOutline, SpeedOutlined, UploadOutlined } from '@vst/beam-icons/icons';
import { useTranslation } from 'react-i18next';

import { hasOfficeHoursTitleAddOns, showTitleAddons } from '@app/utils';

import { selectPlanCharacteristics } from '../../../../redux';

import styles from '../styles.module.scss';
import loadingStyles from './Loading.module.scss';

const Loading = () => (
  <>
    <div className={loadingStyles['smallDetail']} />
    <div className={loadingStyles['smallDetail']} />
    <div className={loadingStyles['smallDetail']} />
    <div className={loadingStyles['largeDetail']} />
  </>
);

export const Characteristics = () => {
  const { t } = useTranslation(['Profile', 'Global']);

  const {
    characteristics: {
      id,
      productFamily,
      uploadSpeed,
      positioning,
      benefits,
      titleAddOns,
      name,
      dataCap,
      isUnleashed,
      downloadSpeed,
      downloadUnits,
    },
    loading,
  } = useSelector(selectPlanCharacteristics);

  const showTitleAddOns = showTitleAddons(productFamily, name, titleAddOns);

  const showGoodFor = positioning?.trim().length > 0;
  const showBestFor = benefits?.trim().length > 0;
  const showPlanRecommendation = showGoodFor || showBestFor;
  const showTitleAddOnsTooltip = hasOfficeHoursTitleAddOns(titleAddOns);

  const tooltip = showTitleAddOnsTooltip ? (
    <Tooltip
      position="top"
      bodyText={t('Global:officeHours_titleAddOns_tooltip')}
      data-cy="tooltip"
      style={{ textAlign: 'left' }}
    >
      <Icon icon={HelpOutline} data-cy="tooltip-icon" size={16} color="gray_600" />
    </Tooltip>
  ) : null;

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      {dataCap && (
        <div className={styles['flexAlignCenter']}>
          <Icon icon={SpeedOutlined} size={20} color="gray_600" />
          <Txt variant={'smallBold'} ml="8px" mr="4px">
            {t('InternetPlan.dataCap', { dataCap })}
          </Txt>
          <Txt variant={'smallRegular'}>{t('InternetPlan.highSpeedData')}</Txt>
        </div>
      )}

      {downloadSpeed && !isUnleashed && (
        <div className={styles['flexAlignCenter']}>
          <Icon icon={DownloadOutlined} size={20} color="gray_600" />
          <Txt variant={'smallRegular'} ml="8px" mr="4px">
            {t('InternetPlan.downloadSpeedUpTo')}
          </Txt>
          <Txt variant="smallBold">{t('InternetPlan.downloadSpeed', { downloadSpeed, downloadUnits })}</Txt>
        </div>
      )}

      {uploadSpeed && (
        <div className={styles['flexAlignCenter']}>
          <Icon icon={UploadOutlined} size={20} color="gray_600" />
          <Txt variant={'smallRegular'} ml="8px" mr="4px">
            {t('InternetPlan.uploadSpeedUpTo')}
          </Txt>
          <Txt variant="smallBold">{t('InternetPlan.uploadSpeed', { uploadSpeed })}</Txt>
        </div>
      )}

      {showTitleAddOns && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            color: colors.teal[600],
          }}
        >
          <Icon icon={AddOutlined} size={20} color="inherit" />
          <Txt variant="smallRegular" ml="8px" mr="4px" color="inherit">
            {titleAddOns}
          </Txt>
          {tooltip}
        </div>
      )}

      {showPlanRecommendation && (
        <div>
          <Txt variant={'smallBold'}>{t('InternetPlan.planRecommendation')}</Txt>
          {showGoodFor && <Txt variant="smallRegular">{positioning}.</Txt>}
          {showBestFor && (
            <Txt variant="smallRegular" ml="4px">
              {benefits}
            </Txt>
          )}
        </div>
      )}
    </>
  );
};
